/**
 * DepartmentQuickCreateDialog - Minimal dialog for quick department creation.
 *
 * Recruiting departments (recruiting_departments) only need a name, so this is the
 * smallest possible Quick Create Dialog. It always skips navigation — it exists to
 * feed the DepartmentPicker's inline-create flow.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { DialogForm } from '../../../shared/components/common';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useCreateDepartment } from '../hooks/useDepartments';
import type { Department } from '../services/recruitingApi';

interface DepartmentQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when the department is created */
  onCreated?: (department: Department) => void;
  /** Pre-fill the name field (e.g., from picker search text) */
  defaultName?: string;
}

interface QuickCreateFormData {
  name: string;
}

export function DepartmentQuickCreateDialog({
  open,
  onClose,
  onCreated,
  defaultName = '',
}: DepartmentQuickCreateDialogProps) {
  const { t } = useTranslation(['common']);
  const createMutation = useCreateDepartment();
  const { handleMutationError } = useApiError();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (open) {
      reset({ name: defaultName });
    }
  }, [open, defaultName, reset]);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const created = await createMutation.mutateAsync({ name: data.name.trim() });
      onCreated?.(created);
      onClose();
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('common:pickers.createDepartmentTitle')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-2">
        <Label htmlFor="department-name" required>{t('common:pickers.departmentName')}</Label>
        <Controller
          name="name"
          control={control}
          rules={{ required: t('common:pickers.departmentNameRequired') }}
          render={({ field }) => (
            <Input
              {...field}
              id="department-name"
              autoFocus
              placeholder={t('common:pickers.departmentNamePlaceholder')}
              className={cn(errors.name && 'border-destructive')}
            />
          )}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>
    </DialogForm>
  );
}
